import { Capability, FeatureFlag } from '__types__';
import { useSelector } from 'react-redux';
import { selectActiveCapabilities } from 'store/userGroups';
import { checkOverallAccess } from './accessCheckers';
import {
  ScopedFeatureFlagServices,
  useScopedFeatureFlagNames
} from './useScopedFeatureFlagNames';

/**
 * Returns true if the current browsing AppScope has the required feature flag
 * and/or the account has the capability.
 */
const useRestrictedAccess = (
  requiredFlag: FeatureFlag | null = null,
  requiredCapabilities: Capability | Capability[] | null = null,
  requiredCapabilitiesAtLeastOne: Capability[] | null = null
) => {
  const activeFeatureFlags = useScopedFeatureFlagNames();
  const activeCapabilities = useSelector(selectActiveCapabilities);

  if (activeFeatureFlags == null) {
    return false;
  }

  return checkOverallAccess(
    requiredFlag,
    requiredCapabilities,
    requiredCapabilitiesAtLeastOne,
    activeFeatureFlags,
    activeCapabilities
  );
};

export const useRestrictedAccessCallback = (
  services?: ScopedFeatureFlagServices
) => {
  const activeFeatureFlags = useScopedFeatureFlagNames(services);
  const activeCapabilities = useSelector(selectActiveCapabilities);

  return (
    requiredFlag: FeatureFlag | null = null,
    requiredCapabilities: Capability | Capability[] | null = null,
    requiredCapabilitiesAtLeastOne: Capability[] | null = null
  ) => {
    if (activeFeatureFlags == null) {
      return false;
    }
    return checkOverallAccess(
      requiredFlag,
      requiredCapabilities,
      requiredCapabilitiesAtLeastOne,
      activeFeatureFlags,
      activeCapabilities
    );
  };
};

export default useRestrictedAccess;

export type UseRestrictedAccessCallback = typeof useRestrictedAccessCallback;
export type Can = ReturnType<UseRestrictedAccessCallback>;
